// FILE: apps/admin/src/pages/FragmentSession.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Reference-matched Fragment.com encrypted session manager with 5 KPI diagnostics cards, Cookie header / Cookie-Editor JSON vault uploader, live session verification, and masked key-value diagnostics table.
//   SCOPE: FragmentSession component.
//   DEPENDS: ../api.js, ../components/Icons.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   FragmentSession - Admin Fragment session control screen with 5 KPI cards, AES-256-GCM cookie saver, and platform_fragment_session diagnostics
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, dt } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

interface FragmentSessionDetailResponse {
  status?: 'loaded' | 'empty' | 'error';
  ready: boolean;
  verified: boolean;
  verificationDetail: string | null;
  hasRecord: boolean;
  source?: 'db' | 'file' | 'none';
  ageDays?: number | null;
  ageHours?: number | null;
  accountLabel?: string | null;
  platformTelegramUserId: number | null;
  updatedByTelegramId?: number | null;
  mode?: string | null;
  kycStatus?: string | null;
  lastRefreshedAt: string | null;
  lastCheckedAt?: string | null;
  lastSessionCheckOkAt: string | null;
  cookieKeysPresent: string[];
}

// START_CONTRACT: FragmentSession
//   PURPOSE: Render the Fragment Session reference page with 5 KPI tiles, encrypted cookie header vault form, and session diagnostics table.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries GET /admin/sessions/fragment and executes POST /admin/sessions/fragment/verify and POST /admin/sessions/fragment/save
//   LINKS: M-ADMIN
// END_CONTRACT: FragmentSession
export function FragmentSession() {
  // START_BLOCK_FRAGMENT_SESSION
  const qc = useQueryClient();
  const toast = useToast();
  const [cookiesInput, setCookiesInput] = useState('');

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['admin-fragment-session'],
    queryFn: () => api.get<FragmentSessionDetailResponse>('/admin/sessions/fragment'),
    refetchInterval: 15_000,
  });

  const verifyMut = useMutation({
    mutationFn: () =>
      api.post<
        Partial<FragmentSessionDetailResponse> & {
          verified?: boolean;
          detail?: string;
        }
      >('/admin/sessions/fragment/verify'),
    onSuccess: (res) => {
      const ok = Boolean(res.verified ?? res.ready);
      const detail = res.verificationDetail ?? res.detail;
      toast(
        ok
          ? `Сессия Fragment подтверждена${detail ? `: ${detail}` : ''}`
          : `Сессия не подтверждена${detail ? `: ${detail}` : ''}`,
      );
      void qc.invalidateQueries({ queryKey: ['admin-fragment-session'] });
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['admin-treasury'] });
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : 'Ошибка проверки сессии Fragment');
    },
  });

  const saveMut = useMutation({
    mutationFn: (cookies: string) =>
      api.post<
        Partial<FragmentSessionDetailResponse> & {
          verified?: boolean;
          detail?: string;
        }
      >('/admin/sessions/fragment/save', { cookies }),
    onSuccess: (res) => {
      setCookiesInput('');
      const ok = Boolean(res.verified ?? res.ready);
      const detail = res.verificationDetail ?? res.detail;
      toast(
        ok
          ? `Cookies зашифрованы (AES-256-GCM) и сессия активна${detail ? ` · ${detail}` : ''}`
          : `Cookies сохранены в БД${detail ? ` · ${detail}` : ''}`,
      );
      void qc.invalidateQueries({ queryKey: ['admin-fragment-session'] });
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['admin-treasury'] });
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : 'Ошибка сохранения cookies Fragment');
    },
  });

  const ageHours =
    data?.ageHours != null
      ? data.ageHours
      : data?.ageDays != null
        ? data.ageDays * 24
        : null;

  const presentKeys = data?.cookieKeysPresent ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Сессия Fragment</h1>
          <div className="page-subtitle">
            Cookies хранятся в БД в зашифрованном виде (AES-256-GCM) и автоматически подхватываются сервисом выдачи.
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn ghost"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <AdminIcon name="refresh" size={14} />
            <span>{isFetching ? 'Обновление…' : 'Обновить'}</span>
          </button>
          <button
            type="button"
            className="btn"
            disabled={verifyMut.isPending}
            onClick={() => verifyMut.mutate()}
          >
            <AdminIcon name="bolt" size={14} />
            <span>{verifyMut.isPending ? 'Проверка…' : 'Перезагрузить сессию'}</span>
          </button>
        </div>
      </div>

      {/* 5 Top KPI Cards */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 175px), 1fr))',
          marginBottom: 18,
        }}
      >
        <div className={`kpi-card ${data?.ready ? 'green' : 'pink'}`}>
          <div className="kpi-label">Статус</div>
          <div
            className="kpi-value"
            style={{ color: data?.ready ? 'var(--green)' : 'var(--red)', fontSize: 19 }}
          >
            {data?.ready ? 'ГОТОВА' : 'НЕ ГОТОВА'}
          </div>
          <div className="kpi-sub mono">ready = {String(Boolean(data?.ready))}</div>
        </div>

        <div className={`kpi-card ${data?.verified ? 'green' : 'amber'}`}>
          <div className="kpi-label">Верификация</div>
          <div
            className="kpi-value"
            style={{
              color: data?.verified ? 'var(--green)' : 'var(--amber)',
              fontSize: 19,
            }}
          >
            {data?.verified ? 'ПОДТВЕРЖДЕНА' : 'НЕ ПРОВЕРЕНА'}
          </div>
          <div className="kpi-sub">{data?.verificationDetail ?? 'Ожидает проверки'}</div>
        </div>

        <div className={`kpi-card ${data?.hasRecord ? 'cyan' : 'pink'}`}>
          <div className="kpi-label">Запись</div>
          <div className="kpi-value" style={{ fontSize: 19 }}>
            {data?.hasRecord ? 'В БД' : 'НЕТ ЗАПИСИ'}
          </div>
          <div className="kpi-sub mono">platform_fragment_session</div>
        </div>

        <div className="kpi-card blue">
          <div className="kpi-label">Возраст</div>
          <div className="kpi-value mono" style={{ fontSize: 19 }}>
            {ageHours != null ? `${ageHours.toFixed(1)} ч` : '—'}
          </div>
          <div className="kpi-sub">с последнего сохранения</div>
        </div>

        <div className="kpi-card purple">
          <div className="kpi-label">Последняя проверка</div>
          <div className="kpi-value mono" style={{ fontSize: 17 }}>
            {data?.lastSessionCheckOkAt ? dt(data.lastSessionCheckOkAt) : '—'}
          </div>
          <div className="kpi-sub">успешный health-check</div>
        </div>
      </div>

      {/* Новые cookies Card */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}
        >
          <h2 style={{ margin: 0 }}>Новые cookies</h2>
          {presentKeys.length > 0 && (
            <div className="row" style={{ gap: 6 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                Загруженные ключи:
              </span>
              {presentKeys.map((keyName) => (
                <span key={keyName} className="chip ok mono" style={{ padding: '2px 8px' }}>
                  <AdminIcon name="lock" size={11} />
                  <span>{keyName}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginTop: 0 }}>
          Как снять cookies: откройте <code>https://fragment.com</code>, войдите через Telegram и
          подключите кошелёк, затем в DevTools → Network скопируйте заголовок <code>Cookie</code>{' '}
          любого запроса к <code>fragment.com</code> (
          <code>stel_ssid=...; stel_dt=...; stel_token=...; stel_ton_token=...</code>) или
          JSON-массив из Cookie-Editor.
        </p>

        <div style={{ marginBottom: 12 }}>
          <label className="field-label">Cookie header</label>
          <textarea
            rows={4}
            className="mono"
            style={{ width: '100%', resize: 'vertical' }}
            placeholder="stel_ssid=...; stel_dt=-180; stel_token=...; stel_ton_token=..."
            value={cookiesInput}
            onChange={(e) => setCookiesInput(e.target.value)}
          />
        </div>

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn primary"
            disabled={!cookiesInput.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate(cookiesInput.trim())}
          >
            <AdminIcon name="lock" size={14} />
            <span>
              {saveMut.isPending ? 'Шифрование и проверка…' : 'Сохранить и перезагрузить'}
            </span>
          </button>
        </div>
      </div>

      {/* Bottom Diagnostics Table Card */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Сессия</h2>
        <div className="table-wrap">
          <table>
            <tbody>
              <tr>
                <td className="muted mono" style={{ width: '38%', minWidth: 140, maxWidth: 240 }}>
                  ready
                </td>
                <td className="mono">
                  <span style={{ color: data?.ready ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                    {String(Boolean(data?.ready))}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="muted mono">verified</td>
                <td className="mono">
                  <span
                    style={{
                      color: data?.verified ? 'var(--green)' : 'var(--amber)',
                      fontWeight: 600,
                    }}
                  >
                    {String(Boolean(data?.verified))}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="muted mono">verificationDetail</td>
                <td>{data?.verificationDetail ?? '—'}</td>
              </tr>
              <tr>
                <td className="muted mono">platformTelegramUserId</td>
                <td className="mono">{data?.platformTelegramUserId ?? '—'}</td>
              </tr>
              <tr>
                <td className="muted mono">mode / kycStatus</td>
                <td className="mono">
                  {data?.mode ?? '—'} · {data?.kycStatus ?? '—'}
                </td>
              </tr>
              <tr>
                <td className="muted mono">cookieKeysPresent</td>
                <td className="mono">{presentKeys.join(', ') || '—'}</td>
              </tr>
              <tr>
                <td className="muted mono">lastRefreshedAt</td>
                <td className="mono">{dt(data?.lastRefreshedAt ?? null)}</td>
              </tr>
              <tr>
                <td className="muted mono">lastSessionCheckOkAt</td>
                <td className="mono">{dt(data?.lastSessionCheckOkAt ?? null)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
  // END_BLOCK_FRAGMENT_SESSION
}
